import React, { useMemo, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { MoreOrganizerEventCard } from './MoreOrganizerEventCard';
import styles from './MoreOrganizerEvents.module.scss';
import { Divider } from '../_shared/Divider';
import { useMoreOrganizerEventsLazy } from './hooks';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { OrganizerEvent } from './types';
import { BasicInfo } from '@/types';
import { getEventsToShow, createTrackingInformationGetter } from './utils';
import { assignRefsToNode } from '@/utils/utils';
import { AffiliateCodes, DOM_IDS } from '../_shared/constants';
import { useTracking, EventInteraction } from '@/tracking';
import { useAppContext } from '../_shared/context/AppContext';
import { isEventEnded, isEventLive } from '../Body/utils/bodyUtils';
import { LISTING_HEAP_IDS } from '@/components/_shared/constants';
import { useComponentVisibilityWithTracking } from '@/hooks/useComponentVisibility';
import { useLocalizedCategory } from '@/hooks';
export const DEFAULT_MAX_EVENTS = 4;

export interface SessionData {
  userId: string | null;
  sessionId: string | null;
  guestId: string | null;
}

export interface MoreOrganizerEventsProps {
  eventId: string;
  isBodyRendered?: boolean;
  eventBasicInfo: BasicInfo;
  category?: string;
  sessionData?: SessionData;
}

const buildOrganizerEventList = (eventsToShow: OrganizerEvent[], affiliateCode?: AffiliateCodes) => {
  return eventsToShow.map((event, index) => {
    const isLastItem = index === eventsToShow.length - 1;
    const eventKey = event.id || `organizer-event-${index}`;

    // Add margin bottom when event is not online and has no venue
    const shouldAddExtraSpace = event.isOnlineEvent === false && !event.venue;

    return (
      <React.Fragment key={eventKey}>
        <div className={shouldAddExtraSpace ? styles.undeterminedVenue : undefined}>
          <MoreOrganizerEventCard organizerEvent={event} affiliateCode={affiliateCode} />
        </div>
        {!isLastItem && <Divider />}
      </React.Fragment>
    );
  });
};

export const MoreOrganizerEvents: React.FC<MoreOrganizerEventsProps> = ({
  eventId,
  isBodyRendered,
  eventBasicInfo,
  category,
  sessionData,
}) => {
  const { formatMessage } = useIntl();
  const {
    data: { locale },
  } = useAppContext();

  const { organizerEvents, isLoading, elementRef } = useMoreOrganizerEventsLazy(
    eventBasicInfo,
    eventId,
    isBodyRendered,
  );

  const eventsToShow = useMemo(() => getEventsToShow(organizerEvents, eventId), [organizerEvents, eventId]);

  const getTrackingInformation = useMemo(() => {
    if (!sessionData) {
      return (eventInteraction: EventInteraction) => ({
        ...eventInteraction,
        user: { locale },
        place: { name: 'loading', position: 0, page: 1, context: {} },
      });
    }

    return createTrackingInformationGetter(
      {
        id: eventBasicInfo.id,
        isOnline: eventBasicInfo.isOnline,
        isEnded: isEventEnded(eventBasicInfo.status),
      },
      organizerEvents?.events || [],
      locale,
      sessionData,
    );
  }, [eventBasicInfo, organizerEvents?.events, locale, sessionData]);

  const { ref: trackingRef } = useTracking<HTMLDivElement>(getTrackingInformation);

  const visibilityTrackingRef = useComponentVisibilityWithTracking(undefined, {
    heap: {
      eventName: LISTING_HEAP_IDS.ORGANIZER_MORE_EVENTS_VIEW,
      eventBucketLabel: 'More events from this organizer',
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (node) {
      assignRefsToNode([elementRef, trackingRef, visibilityTrackingRef], node);
    }
  }, [elementRef, trackingRef, visibilityTrackingRef]);

  const affiliateCode = useMemo(() => {
    if (isEventEnded(eventBasicInfo.status)) {
      return AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED;
    }
    if (isEventLive(eventBasicInfo.status)) {
      return AffiliateCodes.AFF_RELATED_EVENTS_LIVE;
    }
  }, [eventBasicInfo]);

  const organizerEventsList = useMemo(
    () => buildOrganizerEventList(eventsToShow, affiliateCode),
    [eventsToShow, affiliateCode],
  );

  const title = useMemo(() => {
    if (eventBasicInfo.organizer?.name) {
      return formatMessage(
        { id: 'More events from {organizerName}' },
        { organizerName: eventBasicInfo.organizer.name },
      );
    }

    return formatMessage({ id: 'More events from this organizer' });
  }, [eventBasicInfo.organizer?.name, formatMessage]);

  const localizedCategory = useLocalizedCategory(category);

  const subtitle = useMemo(() => {
    if (eventBasicInfo.organizer?.name && localizedCategory) {
      return formatMessage(
        {
          id: 'Discover more events from {organizerName}, from {mainCategory} to other experiences you might love.',
        },
        {
          organizerName: eventBasicInfo.organizer.name,
          mainCategory: localizedCategory,
        },
      );
    }

    return formatMessage({ id: 'Follow organizers to get events picked for you' });
  }, [eventBasicInfo.organizer?.name, formatMessage, localizedCategory]);

  const hasEvents = eventsToShow.length > 0;
  const shouldShowSkeleton = isLoading;
  const shouldShowEmpty = !isLoading && !hasEvents;
  const shouldShowEvents = !isLoading && hasEvents;

  const containerStyle = useMemo(
    () => ({
      ...(shouldShowEmpty && { minHeight: '1px' }),
    }),
    [shouldShowEmpty],
  );

  const containerProps = shouldShowEvents ? { 'data-testid': 'more-organizer-events' } : {};

  // Only render if we have an organizer
  if (!eventBasicInfo.organizer?.id) {
    return null;
  }

  return (
    <div ref={containerRef} style={containerStyle} {...containerProps} id={DOM_IDS.MORE_ORGANIZER_EVENTS}>
      <div className={!shouldShowEvents ? styles.hiddenSeoCopy : ''} data-testid="more-organizer-events-copy">
        <h2 className={styles.title}>{title}</h2>
        <h3 className={styles.subtitle}>{subtitle}</h3>
      </div>
      {shouldShowSkeleton ? <LoadingSkeleton height={550} /> : null}
      {shouldShowEvents ? (
        <section className={styles.moreOrganizerEventsContainer}>
          {organizerEventsList}
          <div className={styles.dividerContainer}>
            <Divider />
          </div>
        </section>
      ) : null}
    </div>
  );
};
